import { Component, OnInit, inject, effect } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { BrandingService } from '@next/core-lib/branding';
import { SiteActions, SiteSelectors, SiteState } from '@next/core-lib/site';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { listAnimations } from '../../../animations';
import { Customer } from '../../../shared/models/shared.model';
import { ObservableTableDataSource } from '../../../shared/table/observable-table-data-source';
import {
  searchCustomers,
  clearSelectedCustomer,
  getCustomersRequest,
} from '../../store/customer.actions';
import {
  getAllCustomers,
  selectLoadingCustomers,
  getSearchText,
} from '../../store/customer.selectors';

@Component({
  selector: 'app-customer-overview',
  templateUrl: './customer-overview.component.html',
  styleUrls: ['./customer-overview.component.scss'],
  animations: [listAnimations],
  standalone: false,
})
export class CustomerOverviewComponent implements OnInit {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly brandingService = inject(BrandingService);

  customers$: Observable<Customer[]> = this.store.select(getAllCustomers);
  readonly customersDataSource = new ObservableTableDataSource(this.customers$);
  readonly displayedColumns = ['name', 'address'];
  emptyCustomers$: Observable<boolean> = this.customers$.pipe(
    map((customers) => !customers || customers.length === 0),
  );
  isLoading$: Observable<boolean> = this.store.select(selectLoadingCustomers);
  readonly pageTitle = toSignal(this.store.select(SiteSelectors.selectPageTitle));
  searchTextValue$: Observable<string> = this.store.select(getSearchText);

  isAgrifirm: boolean = this.brandingService.isTenant('Agrifirm');

  constructor() {
    effect(() => {
      const title = this.activatedRoute.snapshot.data['pageTitle'];
      if (title) {
        this.store.dispatch(SiteActions.updatePageTitle({ pageTitle: title }));
      }
    });
  }

  ngOnInit() {
    this.store.dispatch(clearSelectedCustomer());
    this.store.dispatch(getCustomersRequest({ searchtext: '' }));
  }

  add() {
    this.router.navigate(['add'], { relativeTo: this.activatedRoute });
  }

  openCustomer(customer: Customer) {
    this.router.navigate(['/customer', customer.id]);
  }

  getAddress(customer: Customer) {
    return [customer.street, customer.houseNumber, customer.houseNumberAddition]
      .filter(Boolean)
      .join(' ');
  }

  getCity(customer: Customer) {
    return [customer.postalCode, customer.city].filter(Boolean).join(' ');
  }

  searchValueChanged(searchValue: string) {
    return this.store.dispatch(searchCustomers({ searchtext: searchValue }));
  }
}
